<template>
    <div style="position: relative;">
        <l-map ref="mapRef" @click="focusSatellite = null" :zoom="zoom" @update:zoom="zoom = $event" :center="center"
            :style="{
                height: isFullscreen ? '100vh' : '100%',
                width: '100%',
                zIndex: 0
            }">
            <!-- Satellite Ticker -->
            <div class="satellite-ticker-container">
                <div
                    v-if="tickerItem"
                    :key="tickerItem.id"
                    class="satellite-ticker"
                    @animationend="clearTicker"
                >
                    🛰️ {{ tickerItem.name }}
                </div>
            </div>
            <l-tile-layer :url="url" :attribution="attribution" />
            <l-control-layers ref="control"></l-control-layers>
            <l-marker v-for="satellite in satellites.filter(s => activeSatelliteNames.includes(s.name))"
                :key="satellite.name" :lat-lng="[satellite.lat, satellite.lng]" :icon="getSatelliteIcon(satellite)"
                @click="focusSatellite = satellite.name"
                :visible="!focusSatellite || focusSatellite === satellite.name">
                <l-tooltip :class="focusSatellite ? 'hidden' : ''"
                    :options="{ permanent: true, offset: [12, 0], opacity: 0.5 }">
                    {{ satellite.displayName }}
                </l-tooltip>
            </l-marker>
            <!-- Render a polyline for each segment of the orbit path -->
            <template v-if="orbitPathPoints.length > 0">
                <l-polyline v-for="(segment, index) in orbitPathPoints" :key="`orbit-segment-${index}`"
                    :lat-lngs="segment" color="orange" :weight="2"></l-polyline>
            </template>
            <l-circle v-if="coverageCircleData.visible" :lat-lng="coverageCircleData.center"
                :radius="coverageCircleData.radius" :color="coverageCircleData.color"
                :fillColor="coverageCircleData.fillColor" :fillOpacity="coverageCircleData.fillOpacity"
                :weight="coverageCircleData.weight" />
            <station-layer-groups v-if="stations" :stations="stations" :pinging-satellite-names="pingingSatelliteNames"
                :focus-satellite="focusSatellite" :get-station-icon="getStationIcon"
                :focus-satellite-key="focusSatelliteKey" />
            <!-- Sonar Ping Effect -->
            <l-circle v-for="ping in activePings.filter(p => activeSatelliteNames.includes(p.satelliteName))"
                :key="ping.id" :lat-lng="[ping.lat, ping.lng]" :radius="ping.currentRadius || 0" color="cyan"
                :weight="2" fillColor="cyan" :fillOpacity="ping.opacity || 0" />
            <l-control position="bottomleft" class="satellite-info-control" v-if="focusSatellite">
                <div class="satellite-info-panel" @click.stop>
                    <h3>
                        <router-link :to="`/satellite/${focusSatellite}`">
                            {{satellites.find(s => s.name === focusSatellite)?.displayName}}
                        </router-link>
                    </h3>
                    <div v-if="satellites.find(s => s.name === focusSatellite)?.altitude !== undefined">
                        Altitude: {{satellites.find(s => s.name === focusSatellite)?.altitude.toFixed(0)}} km<br>
                        Speed: {{satellites.find(s => s.name === focusSatellite)?.speed.toFixed(2)}} km/s
                    </div>
                </div>
            </l-control>
            <l-control-scale position="bottomleft" :imperial="true" :metric="true"></l-control-scale>
            <!-- NASA-style Clock Control (centered) -->
            <l-control position="topleft" class="nasa-clock-control">
                <div class="nasa-clock-panel">
                    <div class="nasa-clock-utc">{{ utcTime }}</div>
                    <div class="nasa-clock-local">{{ localTime }}</div>
                </div>
            </l-control>
            <l-control position="topright" class="leaflet-control-sound">
                <div class="leaflet-bar">
                    <select v-model="soundMode" title="Select Sound Effect">
                        <option value="off">Sound Off</option>
                        <option value="beep">Beep</option>
                        <option value="geiger">Geiger</option>
                        <option value="radar">Radar</option>
                        <option value="sonar">Sonar</option>
                    </select>
                </div>
            </l-control>
            <l-control position="bottomright" class="custom-bottomright-controls">
                <div class="bottomright-controls-row">
                    <div class="satellite-filter-dropdown">
                        <label>Satellites:</label>
                        <div @click="showSatelliteDialog = true" class="satellite-filter-dialog-trigger">
                            {{ activeSatelliteNames.length }} selected
                        </div>
                    </div>
                    <div class="fullscreen-control">
                        <button class="fullscreen-button" @click="toggleFullscreen"
                            :title="isFullscreen ? 'Exit fullscreen' : 'Enter fullscreen'">
                            <img :src="require('../assets/fullScren-but.png')" alt="Fullscreen toggle" />
                        </button>
                    </div>
                </div>
            </l-control>
        </l-map>
        
        <!-- Satellite Filter Dialog - Moved outside LMap to avoid stacking context issues -->
        <div v-if="showSatelliteDialog" class="satellite-filter-dialog-overlay" @click.self="showSatelliteDialog = false">
            <div class="satellite-filter-dialog">
                <div class="satellite-filter-dialog-list" @wheel.stop>
                    <div v-for="sat in satellites.slice().sort((a, b) => a.displayName.localeCompare(b.displayName))" :key="sat.name" class="satellite-filter-dialog-item">
                        <input type="checkbox" :id="'sat-' + sat.name" :value="sat.name" v-model="satelliteDialogSelection">
                        <label :for="'sat-' + sat.name">{{ sat.displayName }}</label>
                    </div>
                </div>
                <div class="satellite-filter-dialog-actions">
                    <button @click="satelliteDialogSelection = satellites.map(s => s.name)">All</button>
                    <button @click="satelliteDialogSelection = []">None</button>
                </div>
                <div class="satellite-filter-dialog-footer">
                    <button @click="showSatelliteDialog = false">Cancel</button>
                    <button @click="applySatelliteDialogSelection">Acept</button>
                </div>
            </div>
        </div>
    </div>
</template>

<script setup>
import { ref, watch, onMounted, onBeforeUnmount, reactive, computed } from 'vue'
import { LMap, LTileLayer, LMarker, LTooltip, LControlScale, LControlLayers, LPolyline, LCircle, LControl } from "vue2-leaflet"
import StationLayerGroups from './map/StationLayerGroups.vue'
import axios from '../plugins/axios'
import { useSatelliteTracking } from '../composables/useSatelliteTracking'
import { useMapIcons } from '../composables/useMapIcons'
import { useCoverageCalculation } from '../composables/useCoverageCalculation'
import { useMapSounds } from '../composables/useMapSounds'
import { usePingAnimation } from '../composables/usePingAnimation'

// --- Satellite Ticker Logic ---
const tickerItem = ref(null) // { id, name }
let tickerId = 0
let tickerTimeout = null
function addToTicker(name) {
   tickerItem.value = { id: ++tickerId, name }
   if (tickerTimeout) clearTimeout(tickerTimeout)
   tickerTimeout = setTimeout(() => {
       clearTicker()
   }, 3000)
}
function clearTicker() {
   tickerItem.value = null
   if (tickerTimeout) {
       clearTimeout(tickerTimeout)
       tickerTimeout = null
   }
}
// --- End Satellite Ticker Logic ---

const {
    satellites,
    activeSatelliteNames,
    focusSatellite,
    orbitPathPoints,
    calculateOrbitPathForSatellite
} = useSatelliteTracking()

const { getStationIcon, updateIconSizes, createSatelliteIcon } = useMapIcons()

const { coverageCircleData, updateCoverageCircle } = useCoverageCalculation()
const { pingingSatelliteNames, activePings, createPingForSatellite, cleanup: cleanupPingAnimation } = usePingAnimation()

const zoom = ref(2)
const isFullscreen = ref(false)
const mapRef = ref(null)

// Track which satellites are waving
const wavingSatellites = reactive({})

// Helper to get per-satellite icon with wave animation if needed
function getSatelliteIcon(sat) {
    // Triple size if waving, else normal
    const isWaving = !!wavingSatellites[sat.name]
    const size = isWaving ? 60 : 20
    const color = isWaving ? '#00F0F0' : '#aaFFFF' // Use a consistent color for all satellites
    return createSatelliteIcon(color, size, isWaving)
}

const toggleFullscreen = () => {
    if (!document.fullscreenElement) {
        mapRef.value.$el.requestFullscreen()
        isFullscreen.value = true
    } else {
        document.exitFullscreen()
        isFullscreen.value = false
    }
}

// Listen for fullscreen change events
onMounted(() => {
    document.addEventListener('fullscreenchange', () => {
        isFullscreen.value = !!document.fullscreenElement
    })
})
const center = ref([16, 5])
//const url = ref('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png')
//const attribution = ref('&copy; <a href="http://osm.org/copyright">OpenStreetMap</a> contributors')
const geoUSUrl = 'https://basemap.nationalmap.gov/arcgis/rest/services/USGSImageryOnly/MapServer/tile/{z}/{y}/{x}'
const geoUSAttribution = 'Tiles courtesy of the <a href="https://usgs.gov/">U.S. Geological Survey</a>'
const osmUrl = 'https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png'
const osmAttribution = '&copy; <a href="http://osm.org/copyright">OpenStreetMap</a> contributors'

const url = computed(() => {
    return (zoom.value <= 4) ? geoUSUrl : osmUrl
})
const attribution = computed(() => {
    return (zoom.value <= 4) ? geoUSAttribution : osmAttribution
})

const stations = ref(null)
let eventSource = null

const { soundMode, initializeSounds, playSound } = useMapSounds()

async function initializeSatellitesAndStations() {
    updateIconSizes(zoom.value); // Initialize icon sizes
    try {
        const stationsResponse = await axios.get(`/v1/stations`);
        stations.value = stationsResponse.data;
    } catch (error) {
        console.error("Error initializing stations data:", error);
        stations.value = [];
    }
}

watch(zoom, (newZoom) => {
    updateIconSizes(newZoom)
})

watch(focusSatellite, (newFocusSatelliteName) => {
    if (newFocusSatelliteName) {
        const selectedSat = satellites.value.find(s => s.name === newFocusSatelliteName);
        if (selectedSat) {
            calculateOrbitPathForSatellite(selectedSat);
            updateCoverageCircle(selectedSat); // Update coverage circle for selected satellite
        } else {
            orbitPathPoints.value = []; // Clear path if satellite not found (should not happen ideally)
            updateCoverageCircle(null); // Clear coverage circle
        }
    } else {
        orbitPathPoints.value = []; // Clear path when no satellite is focused
        updateCoverageCircle(null); // Clear coverage circle when no satellite is focused
    }
})

// --- Watch focused satellite position and update coverage circle when it moves ---
const focusedSatelliteObj = computed(() =>
    satellites.value.find(s => s.name === focusSatellite.value)
)

// Key that changes when focused satellite position changes
const focusSatelliteKey = computed(() => {
    const sat = focusedSatelliteObj.value
    if (!sat) return ''
    return `${sat.name}-${sat.lat}-${sat.lng}`
})

watch(
    () => focusedSatelliteObj.value && [focusedSatelliteObj.value.lat, focusedSatelliteObj.value.lng],
    () => {
        if (focusedSatelliteObj.value && focusSatellite.value) {
            updateCoverageCircle(focusedSatelliteObj.value)
        }
    },
    { deep: true }
)

// When satellites list changes, update activeSatelliteNames to include all by default
watch(satellites, (newSats) => {
    if (Array.isArray(newSats) && newSats.length > 0) {
        const savedSelection = loadSatelliteSelection()
        if (savedSelection) {
            // Filter to only include satellites that exist in the current list
            const validSelection = savedSelection.filter(name =>
                newSats.some(s => s.name === name)
            )
            activeSatelliteNames.value = validSelection.length > 0 ? validSelection : newSats.map(s => s.name)
        } else if (activeSatelliteNames.value.length === 0) {
            activeSatelliteNames.value = newSats.map(s => s.name)
        }
    }
}, { immediate: true })

onMounted(() => {
    // Initialize stations, UI and sounds
    initializeSatellitesAndStations();
    initializeSounds();

    // Load saved satellite selection if available
    const savedSelection = loadSatelliteSelection()
    if (savedSelection && Array.isArray(savedSelection)) {
        activeSatelliteNames.value = savedSelection
    }

    // Build EventSource URL from VUE_APP_API_ENDPOINT (proto://host)
    const apiUrl = process.env.VUE_APP_API_ENDPOINT || '';
    const eventSourceUrl = apiUrl + '/sse/events';
    eventSource = new EventSource(eventSourceUrl)
    eventSource.addEventListener('new-packet', (event) => {
        try {
            const packet = JSON.parse(event.data)
            // The satellite name from SSE is the displayName
            if (packet && packet.satellite) {
                // Only trigger sound/ping if satellite is active
                const targetSatellite = satellites.value.find(s => s.displayName === packet.satellite)
                if (!targetSatellite || !activeSatelliteNames.value.includes(targetSatellite.name)) {
                    return; // Ignore if not active
                }

                // Play sound effect if a sound mode is selected
                if (soundMode.value !== 'off') {
                    playSound();
                }

                // Create ping animation for this satellite
                createPingForSatellite(targetSatellite);

                // Show wave animation for 1.5s
                if (targetSatellite) {
                    wavingSatellites[targetSatellite.name] = true
                    setTimeout(() => {
                        wavingSatellites[targetSatellite.name] = false
                    }, 2500)
                    // Add to ticker
                    addToTicker(targetSatellite.displayName)
                }
            }
        } catch (e) {
            console.error("Failed to parse or process SSE packet:", e)
        }
    })
    // Añadir recepción del evento "stations" y mostrarlo por consola
    eventSource.addEventListener('stations', (event) => {
        try {
            const stationsData = JSON.parse(event.data)
            // console.log('SSE event "stations" received')
            stations.value = stationsData
        } catch (e) {
            console.error('Error procesando evento SSE "stations":', e)
        }
    })
})

onBeforeUnmount(() => {
    if (eventSource) {
        eventSource.close()
        eventSource = null
    }
    cleanupPingAnimation()
})

// --- NASA-style Clock Logic ---
const utcTime = ref('')
const localTime = ref('')
let clockInterval = null
function updateClocks() {
    const now = new Date()
    // Format: HH:mm:ss
    utcTime.value = now.toLocaleTimeString('en-GB', { hour12: false, timeZone: 'UTC' }) + ' UTC'
    localTime.value = now.toLocaleTimeString('en-GB', { hour12: false }) + ' Local'
}
onMounted(() => {
    updateClocks()
    clockInterval = setInterval(updateClocks, 1000)
})
onBeforeUnmount(() => {
    if (clockInterval) clearInterval(clockInterval)
})

const showSatelliteDialog = ref(false)
const satelliteDialogSelection = ref([])

watch(showSatelliteDialog, (val) => {
    if (val) {
        // Opened: copy current selection
        satelliteDialogSelection.value = [...activeSatelliteNames.value]
    }
})

function applySatelliteDialogSelection() {
    activeSatelliteNames.value = [...satelliteDialogSelection.value]
    saveSatelliteSelection(activeSatelliteNames.value)
    showSatelliteDialog.value = false
}

// Local storage functions for satellite selection
function saveSatelliteSelection(selection) {
    try {
        localStorage.setItem('tinygs-satellite-selection', JSON.stringify(selection))
    } catch (error) {
        console.error('Failed to save satellite selection to localStorage:', error)
    }
}

function loadSatelliteSelection() {
    try {
        const saved = localStorage.getItem('tinygs-satellite-selection')
        return saved ? JSON.parse(saved) : null
    } catch (error) {
        console.error('Failed to load satellite selection from localStorage:', error)
        return null
    }
}
</script>

<style>
.satellite-ticker-container {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    z-index: 2000;
    pointer-events: none;
    height: 32px;
    display: flex;
    align-items: flex-start;
    overflow: hidden;
}
.satellite-ticker {
    position: relative;
    display: inline-block;
    white-space: nowrap;
    font-size: 18px;
    font-weight: bold;
    color: #fff;
    background: #3556A3;
    border-radius: 0 0 12px 12px;
    margin: 0 8px 0 64px;
    padding: 4px 18px;
    /* animation removed, now static */
    box-shadow: 0 2px 8px rgba(0,0,0,0.15);
    pointer-events: auto;
}
@keyframes ticker-move {
    from {
        transform: translateX(100vw);
        opacity: 1;
    }
    90% {
        opacity: 1;
    }
    to {
        transform: translateX(-100vw);
        opacity: 0;
    }
}

.satellite-icon {
    z-index: 1000 !important;
}

.leaflet-marker-icon[src*="station_icon_green.png"] {
    z-index: 200 !important;
    transition: all 0.3s ease-in-out;
}

.leaflet-marker-icon[src*="station_icon_red.png"] {
    z-index: 199 !important;
    transition: all 0.3s ease-in-out;
}

.station-receiving {
    filter: brightness(1.5) saturate(1.5);
    transform: scale(1.1);
    transition: all 0.3s ease-in-out;
    z-index: 1000 !important;
    /* Mayor que el z-index de los satélites (300) */
}

.hidden {
    display: none;
}

/* Make satellite popups slightly transparent */
.leaflet-popup-content-wrapper {
    background-color: rgba(255, 255, 255, 0.85) !important;
    /* White background with 85% opacity */
    color: #333;
    /* Ensure text is still readable */
}

.leaflet-popup-tip {
    background-color: rgba(255, 255, 255, 0.85) !important;
    /* Match the content wrapper's transparency */
}

.leaflet-control-sound .leaflet-bar {
    background-color: white;
    border-radius: 4px;
    display: flex;
    align-items: center;
    height: 30px;
    /* Match Leaflet's default control height */
}

.leaflet-control-sound select {
    border: none;
    background: transparent;
    height: 100%;
    padding: 0 5px;
    font: 12px "Helvetica Neue", Arial, Helvetica, sans-serif;
    cursor: pointer;
}

.leaflet-control-sound select:focus {
    outline: none;
}

.leaflet-tooltip {
    font-size: 8px !important;
    /* Attempting an even smaller font size */
    padding: 2px 4px !important;
    background-color: white !important;
    border: 1px solid #ccc !important;
    border-radius: 3px !important;
    box-shadow: 0 1px 5px rgba(0, 0, 0, 0.4) !important;
    color: #333 !important;
}

.satellite-filter-control {
    /* Remove manual positioning, let Leaflet handle bottomright */
    position: static !important;
    right: unset !important;
    left: unset !important;
    bottom: unset !important;
    top: unset !important;
    transform: none !important;
    z-index: 1001 !important;
    background: none !important;
    border: none !important;
    box-shadow: none !important;
    pointer-events: none;
}

.custom-bottomright-controls {
    pointer-events: none;
    background: none !important;
    box-shadow: none !important;
    border: none !important;
    position: static !important;
}

.bottomright-controls-row {
    display: flex;
    flex-direction: row;
    align-items: center;
    gap: 10px;
    pointer-events: auto;
}

.satellite-filter-dropdown {
    background: white;
    border-radius: 4px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
    padding: 6px 10px;
    display: flex;
    align-items: center;
    gap: 6px;
    pointer-events: auto;
    min-width: 180px;
    max-width: 350px;
    margin: 0;
}

.satellite-filter-dropdown label {
    font-size: 13px;
    font-weight: 500;
}

.satellite-filter-dropdown select {
    min-width: 140px;
    max-width: 250px;
    font-size: 12px;
    border: 1px solid #ccc;
    border-radius: 3px;
    padding: 2px 4px;
    background: #fafbfc;
}

.satellite-filter-dropdown button {
    font-size: 11px;
    padding: 2px 6px;
    border-radius: 3px;
    border: 1px solid #ccc;
    background: #f5f5f5;
    cursor: pointer;
}

.satellite-filter-dropdown button:hover {
    background: #f0f0f0;
}

.satellite-info-control {
    margin-bottom: 20px !important;
    /* Espacio para el control de escala */
}

.satellite-info-panel {
    background: rgba(255, 255, 255, 0.85);
    padding: 8px 12px;
    border-radius: 4px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
    min-width: 200px;
}

.satellite-info-panel h3 {
    margin: 0 0 4px 0;
    font-size: 14px;
}

.satellite-info-panel a {
    color: #333;
    text-decoration: none;
}

.satellite-info-panel a:hover {
    text-decoration: underline;
}

/* Fullscreen button styles */
.fullscreen-control .fullscreen-button {
    width: 30px;
    height: 30px;
    padding: 3px;
    background: white;
    border: 2px solid rgba(0, 0, 0, 0.2);
    border-radius: 4px;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
}

.fullscreen-control .fullscreen-button:hover {
    background: #f4f4f4;
}

.fullscreen-control .fullscreen-button img {
    width: 16px;
    height: 16px;
    object-fit: contain;
}
@import url('https://fonts.googleapis.com/css2?family=Orbitron:wght@700&display=swap');
@import url('https://fonts.googleapis.com/css2?family=Share+Tech+Mono&display=swap');

/* Target both the control and the Leaflet container */
.leaflet-top {
    width: 100% !important;
    pointer-events: none;
}

.nasa-clock-control {
    position: absolute !important;
    left: 0 !important;
    right: 0 !important;
    width: 100% !important;
    margin: 10px 0 0 0 !important;
    text-align: center !important;
    float: none !important;
    clear: none !important;
    z-index: 3000 !important;
    pointer-events: none;
}

.nasa-clock-control .nasa-clock-panel {
    display: inline-block !important;
    margin: 0 auto !important;
    text-align: center !important;
}
.nasa-clock-panel {
    background: rgba(0,0,0,0.92);
    border-radius: 8px;
    padding: 12px 36px 10px 36px;
    display: flex;
    flex-direction: column;
    align-items: center;
    min-width: 180px;
    pointer-events: auto;
    box-shadow: 0 4px 16px rgba(0,0,0,0.35);
    opacity: 0.8;
}
.nasa-clock-utc {
    font-family: 'Share Tech Mono', 'Fira Mono', 'Consolas', 'Menlo', 'Monaco', 'Courier New', Courier, monospace;
    font-size: 2.8em;
    color: #00ff00;
    letter-spacing: 0.12em;
    text-shadow: 0 0 12px #00ff00, 0 0 3px #000;
    line-height: 1.1;
    margin-bottom: 4px;
}
.nasa-clock-local {
    font-family: 'Share Tech Mono', 'Fira Mono', 'Consolas', 'Menlo', 'Monaco', 'Courier New', Courier, monospace;
    font-size: 1.5em;
    color: #ff3333;
    letter-spacing: 0.10em;
    text-shadow: 0 0 8px #ff3333, 0 0 2px #000;
    opacity: 0.85;
    line-height: 1.1;
}

/* Responsive: Reduce NASA clock size on small screens */
@media (max-width: 600px) {
  .nasa-clock-panel {
    padding: 6px 12px 6px 12px;
    min-width: 110px;
  }
  .nasa-clock-utc {
    font-size: 1.3em;
    margin-bottom: 2px;
  }
  .nasa-clock-local {
    font-size: 0.95em;
  }
}

.satellite-filter-dialog-trigger {
    min-width: 90px;
    padding: 4px 10px;
    border: 1px solid #ccc;
    border-radius: 3px;
    background: #fafbfc;
    cursor: pointer;
    font-size: 12px;
    text-align: left;
}
.satellite-filter-dialog-overlay {
    position: fixed;
    top: 0; left: 0; right: 0; bottom: 0;
    background: rgba(0,0,0,0.18);
    z-index: 9999;
    display: flex;
    align-items: center;
    justify-content: center;
}
.satellite-filter-dialog {
    background: white;
    border-radius: 12px;
    box-shadow: 0 4px 24px rgba(0,0,0,0.18);
    padding: 18px 18px 10px 18px;
    min-width: 320px;
    max-width: 95vw;
    max-height: 80vh;
    display: flex;
    flex-direction: column;
    gap: 10px;
    z-index: 99999;
}
.satellite-filter-dialog-list {
    overflow-y: auto;
    max-height: 45vh;
    margin-bottom: 8px;
}
.satellite-filter-dialog-item {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 2px 0;
}
.satellite-filter-dialog-actions {
    display: flex;
    gap: 8px;
    margin-bottom: 8px;
}
.satellite-filter-dialog-footer {
    display: flex;
    justify-content: flex-end;
    gap: 10px;
}
.satellite-filter-dialog-footer button {
    min-width: 80px;
}

.bottomright-controls-row {
    display: flex;
    align-items: center;
    gap: 10px;
}
</style>